// A video's real first frame, its size and its length, worked out on this device before it is
// encrypted. The frame becomes the same 320 px preview and tiny placeholder photos have.

import type { FileMeta } from "@rhema/client";
import { previews } from "./image";
import { browserRaster, type Raster } from "./raster";

/** Where the poster frame is taken from. The very first frame is often black. */
export const POSTER_AT = 0.1;
/** As long as describing a file took before. */
export const VIDEO_MS = 8000;

/** The parts of a video element this uses, so tests can pass their own. */
export interface VideoLike extends EventTarget {
  muted: boolean;
  playsInline: boolean;
  preload: string;
  src: string;
  currentTime: number;
  readonly duration: number;
  readonly videoWidth: number;
  readonly videoHeight: number;
  readonly readyState: number;
  removeAttribute(name: string): void;
  load(): void;
  requestVideoFrameCallback?(cb: () => void): number;
}

function once(target: EventTarget, event: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const ok = () => {
      target.removeEventListener("error", bad);
      resolve();
    };
    const bad = () => {
      target.removeEventListener(event, ok);
      reject(new Error("This video could not be read."));
    };
    target.addEventListener(event, ok, { once: true });
    target.addEventListener("error", bad, { once: true });
  });
}

/** Waits until the seeked frame is painted, where the browser can say so. */
function framePainted(video: VideoLike): Promise<void> {
  if (typeof video.requestVideoFrameCallback !== "function") return Promise.resolve();
  return Promise.race([
    new Promise<void>((resolve) => video.requestVideoFrameCallback?.(() => resolve())),
    // Paused videos on some browsers never call back. The frame is ready by then anyway.
    new Promise<void>((resolve) => setTimeout(resolve, 300)),
  ]);
}

export type VideoOptions = {
  raster?: Raster;
  makeVideo?: () => VideoLike;
  objectUrl?: (file: Blob) => string;
  revokeUrl?: (url: string) => void;
  timeoutMs?: number;
};

async function describe(file: Blob, opts: Required<Omit<VideoOptions, "timeoutMs">>): Promise<FileMeta> {
  const url = opts.objectUrl(file);
  const video = opts.makeVideo();
  try {
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    const loaded = once(video, "loadeddata");
    video.src = url;
    await loaded;
    const width = video.videoWidth;
    const height = video.videoHeight;
    const meta: FileMeta = {
      width,
      height,
      ...(Number.isFinite(video.duration) ? { duration: Math.round(video.duration * 10) / 10 } : {}),
    };
    if (!width || !height) return meta;
    const seeked = once(video, "seeked");
    video.currentTime = Math.min(POSTER_AT, (video.duration || 1) / 2);
    await seeked;
    await framePainted(video);
    return { ...meta, ...(await previews(opts.raster, { width, height, source: video })) };
  } finally {
    video.removeAttribute("src");
    video.load();
    opts.revokeUrl(url);
  }
}

/** Size, length, a real first-frame preview and a placeholder. Gives what it could in time. */
export async function describeVideo(file: Blob, opts: VideoOptions = {}): Promise<FileMeta> {
  const name = (file as File).name || undefined;
  const base: FileMeta = { mime: file.type || "video/mp4", ...(name ? { name } : {}) };
  const full = {
    raster: opts.raster ?? browserRaster,
    makeVideo: opts.makeVideo ?? (() => document.createElement("video") as unknown as VideoLike),
    objectUrl: opts.objectUrl ?? ((f: Blob) => URL.createObjectURL(f)),
    revokeUrl: opts.revokeUrl ?? ((u: string) => URL.revokeObjectURL(u)),
  };
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const meta = await Promise.race([
      describe(file, full),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error("timeout")), opts.timeoutMs ?? VIDEO_MS);
      }),
    ]);
    return { ...base, ...meta };
  } catch {
    return base;
  } finally {
    clearTimeout(timer);
  }
}
